import React, { useEffect, useState } from "react";
import { Button, toast, Notification, IconName } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { FORM_ID } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { CustomerCreditCheck as CustomerCreditCheckType } from "../../../../../../models/state";
import { FieldSet, FormRow, FormRowFormats, Input } from "../../../../forms";
import { isTypeLoading } from "../../../../loading/state/selectors";
import { RapidForm, actionTypes as RF_AT } from "../../../../rapidform";
import { VALIDATION_FIELD, allValidationRules, validateField } from "../../../../rapidform/formValidation";
import { handleCreditCheck, handleCustomerLookup } from "../../../state/actions";
import { RETAILER_PERFORM_CREDIT_CHECK } from "../../../state/actionTypes";
import {
    getCustomerCreditCheck,
    getCustomerLegalOwnerIndividual,
    getExistingPhoneNumberLookup,
    getIsPrepaid,
    getCustomerExistingAccounts,
    getCustomerLookupPerformed,
    getCustomerManualResult,
} from "../../../state/selectors/customerSelectors";
import { requiresCreditCheck as requiresCreditCheckSelector } from "../../../state/selectors/creditCheckSelectors";
import { getFormById } from "../../../state/selectors/formSelectors";
import TEXT from "../../../text-consts";
import "./CustomerCreditCheck.less";
import ContactCard from "../ContactCard/ContactCard";
import { CustomerSection } from "../CustomerSection";
import ProductUserSubscriptionModal from "../ProductUserSubscriptionModal/ProductUserSubscriptionModal";

const getDisplayableCreditCheckResult = (
    creditCheck?: CustomerCreditCheckType,
    isExistingCustomer?: boolean,
): {
    heading?: string;
    icon: IconName;
    message?: string;
    variant: "success" | "error" | "beige" | "warning" | "gray" | "teal" | "information";
} => {
    switch (creditCheck?.decision) {
        case "OK":
            return {
                variant: "success",
                message: `Kredittsjekk godkjent`,
                icon: "check-circle",
            };
        case "CONTROL":
            return {
                heading: "Manuell kontroll påkrevd",
                variant: "information",
                message: creditCheck.message,
                icon: "info",
            };
        case "OK_WITH_CREDITLIMIT":
            return {
                heading: "Kredittsjekk godkjent med kredittgrense",
                variant: "warning",
                message: creditCheck.message,
                icon: "alert",
            };
        case "SCORE_NOT_AVAILABLE":
            return {
                heading: "Kunden har reservert seg mot å bli kredittsjekket.",
                variant: "error",
                message:
                    "Vi kan ikke fullføre din ordre før denne reservasjonen er fjernet. Sjekk ut <a href='https://www.minexperian.no/Home/FAQ#:~:text=ta%20opp%20l%C3%A5n.-,Hvordan%20opphever%20man%20en%20kredittsperre%3F,-Oppheving%20av%20en'>Experian sine sider</a> for mer informasjon om hvordan man opphever sperren.",
                icon: "info",
            };
        case "DENIED":
            return {
                heading: "Kredittsjekk avslått",
                variant: isExistingCustomer ? "warning" : "error",
                message: creditCheck.message,
                icon: "error",
            };
        case "INVALID_NATIONAL_IDENTITY_NUMBER":
            return {
                heading: "Kredittsjekk avslått",
                variant: isExistingCustomer ? "warning" : "error",
                message: creditCheck.message,
                icon: "error",
            };
        default:
            return { variant: "beige", heading: `Klarte ikke å hente kredittsjekkstatus`, icon: "question" };
    }
};

const getHeading = ({
    creditCheckNeeded,
    creditCheckPerformed,
    existingPhoneNumberLookup,
    hasLegalOwner,
    isPrepaid,
    showCreditCheckForm,
    isExistingCustomer,
    isCustomerLookupPerformed,
}: {
    creditCheckNeeded: boolean;
    creditCheckPerformed?: boolean;
    existingPhoneNumberLookup: string | undefined;
    hasLegalOwner: boolean;
    isPrepaid: boolean;
    showCreditCheckForm: boolean;
    isExistingCustomer: boolean;
    isCustomerLookupPerformed: boolean;
}): string => {
    if (isPrepaid && !creditCheckNeeded) return `Bestiller av kontantkortet`;
    if (existingPhoneNumberLookup && hasLegalOwner && !creditCheckPerformed)
        return `Eier av nummer: ${existingPhoneNumberLookup}`;
    if (showCreditCheckForm && isExistingCustomer && creditCheckPerformed) return "Gjennomfør kredittsjekk";
    if (showCreditCheckForm && isExistingCustomer && !creditCheckNeeded) return "Gjennomfør kundeoppslag";
    if (showCreditCheckForm && creditCheckNeeded && existingPhoneNumberLookup)
        return `Kredittsjekk ved endring av eksisterende abonnement (${existingPhoneNumberLookup})`;
    if (showCreditCheckForm && creditCheckNeeded) return "Gjennomfør kredittsjekk";
    if (!showCreditCheckForm && creditCheckPerformed) return "Resultat av kredittsjekk";
    if (!showCreditCheckForm && isCustomerLookupPerformed && !creditCheckPerformed) return "Kundeoppslag utført";
    if (!creditCheckNeeded) return "Resultat av kundeoppslag";
    return "Gjennomfør kundeoppslag";
};

const getSubmitButtonText = ({
    isExistingCustomer,
    creditCheckPerformed,
    creditCheckNeeded,
    isPrepaid,
}: {
    isExistingCustomer: boolean;
    creditCheckPerformed?: boolean;
    creditCheckNeeded: boolean;
    isPrepaid: boolean;
}): string => {
    if (isExistingCustomer && creditCheckPerformed) return "Gjennomfør kredittsjekk";
    if (isExistingCustomer) return "Hent kundedata";
    if (creditCheckNeeded) return "Gjennomfør kredittsjekk";
    if (isPrepaid) return "Søk";
    return "Hent kundeinfo";
};

// Accepts both birth numbers and D-numbers
const getSsnErrorMessage = (ssn: string): string | undefined => {
    const errorMessage = validateField(ssn, allValidationRules[VALIDATION_FIELD.SSN]);
    return typeof errorMessage === "string" ? errorMessage : undefined;
};

const CustomerCreditCheck = () => {
    const form = useSelector((state: RootState) => getFormById(state, FORM_ID.CREDIT_CHECK));
    const isPerformingCreditCheck = useSelector((state: RootState) =>
        isTypeLoading(state, RETAILER_PERFORM_CREDIT_CHECK),
    );
    const creditCheck = useSelector((state: RootState) => getCustomerCreditCheck(state));
    const isCustomerLookupPerformed = useSelector((state: RootState) => getCustomerLookupPerformed(state));
    const legalOwner = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state));
    const manualResult = useSelector((state: RootState) => getCustomerManualResult(state));
    const isPrepaid = useSelector((state: RootState) => getIsPrepaid(state));
    const existingPhoneNumberLookup = useSelector((state: RootState) => getExistingPhoneNumberLookup(state));
    const creditCheckNeeded = useSelector((state: RootState) => requiresCreditCheckSelector(state));
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const hasLegalOwner = !!legalOwner?.firstName;
    const needsManualAddress = !!creditCheck?.requireManuallyAddress;
    const [error, setError] = useState<string | null>(null);
    // Hide form if either credit check OR customer lookup has been performed, unless a manual address is still owed
    const [showCreditCheckForm, setShowCreditCheckForm] = useState(
        needsManualAddress || (!creditCheck?.performed && !isCustomerLookupPerformed),
    );
    const [isProductUserLookup, setIsProductUserLookup] = useState(false);
    const [isProductUserModalOpen, setIsProductUserModalOpen] = useState(false);
    const dispatch = useDispatch();
    const firstNameInputRef = React.useRef<HTMLInputElement>(null);
    const ssnInputRef = React.useRef<HTMLInputElement>(null);

    const isExistingCustomer = existingAccounts.length > 0;

    // Update form visibility when customer lookup state changes (e.g., on page refresh)
    useEffect(() => {
        // A manual address is still owed, so the form (name/ssn/address) must stay visible regardless of lookup state
        if (needsManualAddress) {
            setShowCreditCheckForm(true);
            return;
        }
        // For existing customers: never show form if customer lookup was performed (manualResult exists)
        if (isExistingCustomer && (isCustomerLookupPerformed || manualResult)) {
            setShowCreditCheckForm(false);
        } else if (creditCheck?.performed) {
            // Credit check performed - hide form for all customer types
            setShowCreditCheckForm(false);
        }
    }, [needsManualAddress, isExistingCustomer, isCustomerLookupPerformed, manualResult, creditCheck?.performed]);

    const heading = getHeading({
        creditCheckNeeded,
        creditCheckPerformed: creditCheck?.performed,
        existingPhoneNumberLookup,
        hasLegalOwner,
        isPrepaid,
        showCreditCheckForm,
        isExistingCustomer,
        isCustomerLookupPerformed,
    });
    const description = needsManualAddress
        ? "Kunden ble ikke funnet med adresse i Folkeregisteret. Vennligst oppgi adressen manuelt for å fullføre kredittsjekken."
        : creditCheck?.performed && existingPhoneNumberLookup && !legalOwner?.firstName
          ? "Telefonnummer som er søkt opp tilhører en bruker som er forskjellig fra juridisk eier. For kredittsjekk og kundeoppslag må navn og fødselsnummer / D-nummer til juridisk eier oppgis."
          : "";
    const customerDetails = (form?.values?.["customerDetails"] as Record<string, string>) || {};

    const handleSubmit = async () => {
        try {
            setError(null);
            setIsProductUserLookup(false);
            const firstName = customerDetails["firstName"] || "";
            const lastName = customerDetails["lastName"] || "";
            const ssn = customerDetails["ssn"] || "";

            const ssnErrorMessage = getSsnErrorMessage(ssn);
            if (ssnErrorMessage) {
                setError(ssnErrorMessage);
                return;
            }

            const addressDetails = (form?.values?.["address"] as Record<string, string>) || {};
            const manualAddress = needsManualAddress
                ? {
                      street: addressDetails["street"] || "",
                      postcode: addressDetails["postcode"] || "",
                      city: addressDetails["city"] || "",
                  }
                : undefined;

            if (manualAddress && (!manualAddress.street || !manualAddress.postcode || !manualAddress.city)) {
                setError("Gateadresse, postnummer og poststed er påkrevd.");
                return;
            }

            let stillNeedsManualAddress = false;

            // For existing customers: use customer lookup first, then perform a credit check when required.
            if (isExistingCustomer && !needsManualAddress && !creditCheck?.performed) {
                const lookupResult = await dispatch(handleCustomerLookup(firstName, lastName, ssn));
                if (lookupResult.isProductUserLookup) {
                    setIsProductUserLookup(true);
                    return;
                }
            } else {
                // New customers and existing customers with a previous credit check require a full check.
                const shouldForceCreditCheck = !isExistingCustomer || !!creditCheck?.performed;
                const result = await dispatch(
                    handleCreditCheck(
                        shouldForceCreditCheck,
                        isExistingCustomer && !needsManualAddress
                            ? undefined
                            : {
                                  firstName,
                                  lastName,
                                  nationalIdentificationNumber: ssn,
                                  ...manualAddress,
                              },
                    ),
                );
                stillNeedsManualAddress = !!result.requireManuallyAddress;
            }

            if (stillNeedsManualAddress) {
                return;
            }
            setShowCreditCheckForm(false);

            let message = "Kredittsjekk fullført";
            if (isExistingCustomer && !creditCheck?.performed) {
                message = "Kundeoppslag fullført";
            } else if (isExistingCustomer && creditCheck?.performed) {
                message = "Kredittsjekk fullført";
            } else if (!creditCheckNeeded) {
                message = "Kundeoppslag fullført";
            }

            toast.success(message, {
                position: "top-center",
            });
        } catch (e: any) {
            setError(e?.displayable ? e.error : TEXT.ERRORS.CREDIT_CHECK_ERROR);
        }
    };

    useEffect(() => {
        // Don't auto-submit for existing customers with manual result.
        if (isExistingCustomer && manualResult) {
            return;
        }
        // Don't auto-submit when a manual address is still owed - the ssn is pre-filled but the form isn't complete yet
        if (needsManualAddress) {
            return;
        }

        const firstName = customerDetails["firstName"] || "";
        const lastName = customerDetails["lastName"] || "";
        const ssn = customerDetails["ssn"] || "";
        if (showCreditCheckForm && firstName.trim() && lastName.trim() && !getSsnErrorMessage(ssn)) {
            handleSubmit();
        }
    }, [form?.values?.["customerDetails"]?.["ssn"]]);

    const creditCheckResult = getDisplayableCreditCheckResult(creditCheck, isExistingCustomer);

    return (
        <CustomerSection className="customer-credit-check" heading={heading} description={description} error={error}>
            {hasLegalOwner && (
                <ContactCard
                    name={`${legalOwner.firstName || ""} ${legalOwner.lastName || ""}`.trim()}
                    address={legalOwner.street || ""}
                    city={legalOwner.city || ""}
                    showTriggerButton={!showCreditCheckForm && !isExistingCustomer}
                    triggerFunction={() => {
                        dispatch({
                            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CREDIT_CHECK),
                            payload: {
                                errorMessage: undefined,
                                form: FORM_ID.CREDIT_CHECK,
                                inputName: "customerDetails-ssn",
                                inputValue: "",
                                visible: true,
                            },
                        });
                        setShowCreditCheckForm(true);
                    }}
                    triggerText={
                        isExistingCustomer ? "Nytt oppslag" : !creditCheckNeeded ? "Nytt oppslag" : "Ny kredittsjekk"
                    }
                />
            )}
            {showCreditCheckForm && (needsManualAddress || !(isExistingCustomer && manualResult)) && (
                <RapidForm
                    id={FORM_ID.CREDIT_CHECK}
                    action={"#"}
                    values={form?.values || {}}
                    visible={form?.visible || {}}
                    started={form?.started || {}}
                    errors={form?.errors || {}}
                    onSubmit={handleSubmit}
                >
                    <FieldSet name="customerDetails">
                        {!hasLegalOwner && (
                            <div className="customer-credit-check__name">
                                <Input
                                    className="customer-credit-check__name-input"
                                    type="text"
                                    name="firstName"
                                    label="Fornavn"
                                    disabled={false}
                                    data-di-mask
                                    inputRef={firstNameInputRef}
                                />
                                <Input
                                    className="customer-credit-check__name-input"
                                    type="text"
                                    name="lastName"
                                    label="Etternavn"
                                    disabled={false}
                                    data-di-mask
                                />
                            </div>
                        )}
                        <Input
                            type="text"
                            name="ssn"
                            label="Fødselsnummer / D-nummer"
                            disabled={false}
                            data-di-mask
                            maxlength={11}
                            inputRef={ssnInputRef}
                        />
                    </FieldSet>
                    {needsManualAddress && (
                        <FieldSet name="address">
                            <Input type="text" name="street" label="Gateadresse" data-di-mask />
                            <FormRow format={FormRowFormats.FORTY_SIXTY} data-di-mask>
                                <Input type="text" name="postcode" label="Postnummer" maxlength={4} />
                                <Input type="text" name="city" label="Poststed" />
                            </FormRow>
                        </FieldSet>
                    )}
                    <div className="customer-credit-check__action">
                        <Button
                            mt="300"
                            type="submit"
                            variant="primary"
                            loading={isPerformingCreditCheck}
                            form={FORM_ID.CREDIT_CHECK}
                        >
                            {getSubmitButtonText({
                                isExistingCustomer,
                                creditCheckPerformed: creditCheck?.performed,
                                creditCheckNeeded,
                                isPrepaid,
                            })}
                        </Button>
                    </div>
                </RapidForm>
            )}
            {isProductUserLookup && (
                <>
                    <Notification
                        variant="information"
                        mt="200"
                        className="customer-credit-check__product-user-notification"
                    >
                        <Notification.Icon name="info" />
                        <Notification.Text>
                            Fødselsnummeret / D-nummeret du skrev inn tilhører brukeren, men ikke den juridiske eieren
                            av telefonnummeret. Dette gir deg kun tilgang til å se informasjon om abonnementet til
                            brukeren. Om du ønsker å gjøre endringer, må fødselsnummeret til den juridiske eieren
                            skrives inn.
                        </Notification.Text>

                        <Notification.Footer>
                            <Button mt="200" variant="secondary" onClick={() => setIsProductUserModalOpen(true)}>
                                Se info om brukeren
                            </Button>
                        </Notification.Footer>
                    </Notification>

                    <ProductUserSubscriptionModal
                        open={isProductUserModalOpen}
                        onClose={() => setIsProductUserModalOpen(false)}
                    />
                </>
            )}
            {isExistingCustomer && isCustomerLookupPerformed && !isProductUserLookup && (
                <Notification type="tile" variant="success" mt="200" className="identification__notification">
                    <Notification.Icon name="check-circle" />
                    <Notification.Text>Kundeoppslag fullført</Notification.Text>
                </Notification>
            )}
            {creditCheck?.performed && !showCreditCheckForm && (
                <>
                    <Notification
                        type="tile"
                        variant={creditCheckResult.variant}
                        mt="200"
                        className="identification__notification"
                    >
                        <Notification.Icon name={creditCheckResult.icon} />
                        <Notification.Text>
                            {creditCheckResult.heading && (
                                <>
                                    <strong>{creditCheckResult.heading}</strong>
                                    <br />
                                </>
                            )}
                            {creditCheckResult.message && (
                                <>
                                    {creditCheck?.decision === "SCORE_NOT_AVAILABLE" ||
                                    creditCheck.decision === "DENIED" ? (
                                        <span dangerouslySetInnerHTML={{ __html: creditCheckResult.message }} />
                                    ) : (
                                        creditCheckResult.message
                                    )}
                                </>
                            )}
                        </Notification.Text>
                    </Notification>
                    {creditCheck.leaseRestrictionAmount !== undefined && creditCheck.decision !== "DENIED" && (
                        <Notification
                            type="tile"
                            variant="information"
                            mt="200"
                            className="identification__notification"
                        >
                            <Notification.Icon name="alert" />
                            <Notification.Text>
                                Maks tillatt delbetalingsbeløp:{" "}
                                <strong>{creditCheck.leaseRestrictionAmount.toLocaleString("nb-NO")} kr</strong>
                            </Notification.Text>
                        </Notification>
                    )}
                </>
            )}
        </CustomerSection>
    );
};

export default CustomerCreditCheck;
